/**
 * RELEASE REPORT
 *
 * Execution report view for a specific release.
 * Displays summary metrics and status distribution.
 */

import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { token } from '@atlaskit/tokens'
import Spinner from '@atlaskit/spinner'
import Lozenge from '@atlaskit/lozenge'

import StatusPieChart from './charts/StatusPieChart'
import PassRateGauge from './charts/PassRateGauge'
import MetricCardGrid, { createMetricsFromSummary, formatLastExecutionDate } from './cards/MetricCardGrid'
import ReportTabs from './ReportTabs'
import TrendsTab from './tabs/TrendsTab'
import DefectsTab from './tabs/DefectsTab'
import PeopleTab from './tabs/PeopleTab'
import TopFailingTab from './tabs/TopFailingTab'
import {
  fetchReleaseReportAsync,
  selectReleaseReport,
  selectCurrentReportTab,
  selectReportLoading,
  selectReportErrors,
  openDrillDown
} from '../../store/slices/reportsSlice'

/**
 * Release status to Lozenge appearance mapping
 */
const STATUS_APPEARANCE = {
  'Draft': 'default',
  'Active': 'inprogress',
  'Released': 'success',
  'Archived': 'moved'
}

/**
 * ReleaseReport Component
 *
 * @param {object} props
 * @param {number|string} props.releaseId - Release ID
 */
const ReleaseReport = ({ releaseId }) => {
  const dispatch = useDispatch()

  // Selectors
  const report = useSelector(selectReleaseReport)
  const currentTab = useSelector(selectCurrentReportTab)
  const loading = useSelector(selectReportLoading)
  const errors = useSelector(selectReportErrors)

  // Fetch data whenever the release changes.
  useEffect(() => {
    if (releaseId) {
      dispatch(fetchReleaseReportAsync({ releaseId }))
    }
  }, [dispatch, releaseId])

  if (!report) {
    if (loading.release) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '300px'
        }}>
          <Spinner size="large" />
        </div>
      )
    }
    if (errors.release) {
      return (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: token('color.text.danger', '#DE350B')
        }}>
          <p>Failed to load release report</p>
          <p style={{ fontSize: '12px', color: token('color.text.subtle', '#6B778C') }}>
            {errors.release}
          </p>
        </div>
      )
    }
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: token('color.text.subtle', '#6B778C')
      }}>
        No report data available
      </div>
    )
  }

  const { summary } = report
  const metrics = createMetricsFromSummary(summary)

  const extendedMetrics = [
    ...metrics.slice(0, 3),
    { value: summary.totalScenarios, label: 'Scenarios' },
    ...metrics.slice(3)
  ]

  return (
    <div style={{ padding: '0' }}>
      {/* Header info — persistent across tabs. */}
      <div style={{
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: `1px solid ${token('color.border', '#DFE1E6')}`
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: 600,
          color: token('color.text', '#172B4D')
        }}>
          {report.releaseName}
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginTop: '8px'
        }}>
          <Lozenge appearance={STATUS_APPEARANCE[report.releaseStatus] || 'default'}>
            {report.releaseStatus}
          </Lozenge>
          {report.archived && (
            <span style={{
              fontSize: '12px',
              color: token('color.text.subtle', '#6B778C')
            }}>
              (Archived)
            </span>
          )}
        </div>
      </div>

      <ReportTabs level="release" />

      {currentTab === 'trends' && (
        <TrendsTab level="release" id={releaseId} />
      )}

      {currentTab === 'defects' && (
        <DefectsTab level="release" id={releaseId} />
      )}

      {currentTab === 'people' && (
        <PeopleTab level="release" id={releaseId} />
      )}

      {currentTab === 'topFailing' && (
        <TopFailingTab releaseId={releaseId} />
      )}

      {currentTab === 'overview' && (
        <>
          {/* Charts row */}
          <div style={{
            display: 'flex',
            gap: '32px',
            marginBottom: '24px',
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <div style={{ flex: '1 1 280px', minWidth: '280px', maxWidth: '350px' }}>
              <StatusPieChart
                statusDistribution={summary.statusDistribution}
                height={260}
                centerLabel="Executions"
                onSegmentClick={(statusName) =>
                  dispatch(openDrillDown({ level: 'release', id: releaseId, status: [statusName] }))
                }
              />
            </div>

            <div style={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PassRateGauge
                passRate={summary.passRate}
                size={180}
                showStatus={true}
              />
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <MetricCardGrid metrics={extendedMetrics} compact={true} gap="12px" />
          </div>

          <div style={{
            fontSize: '13px',
            color: token('color.text.subtle', '#6B778C')
          }}>
            Last Execution: {formatLastExecutionDate(summary.lastExecutionDate)}
          </div>
        </>
      )}
    </div>
  )
}

export default ReleaseReport
